/**
 * Browser-side image compression via the canvas API. No external deps.
 *
 * Each preset matches the storage rule expectations + the visual needs of
 * the consuming surface:
 *
 * - **headshot** — speaker / user profile photos. 1200px max dimension,
 *   JPEG q85, target ~200-300 KB. Source headshots are often 2000-4000px
 *   from professional shoots; we cap dimensions and recompress.
 *
 * - **hero** — event banner / hero images. 1920px max dimension, JPEG q85,
 *   target ~400 KB. Wider gamut so cards and full-bleed banners both look good.
 *
 * - **logo** — sponsor logos. 800px max dimension, attempts to preserve
 *   PNG transparency for files under 500 KB; otherwise re-encodes to JPEG q90.
 *   Logos that are already small vectors-as-PNGs are passed through untouched.
 *
 * The 10 MB size cap in storage rules is the safety net — these presets all
 * land well below it for any realistic source.
 */

export type CompressionPreset = "headshot" | "hero" | "logo";

interface PresetConfig {
  maxDimension: number;
  /** Output format. "preserve-png" keeps PNG if source is PNG and small enough. */
  format: "image/jpeg" | "image/png" | "preserve-png";
  /** Output quality (0-1) for JPEG encoding. Ignored for PNG. */
  quality: number;
}

const PRESETS: Record<CompressionPreset, PresetConfig> = {
  headshot: { maxDimension: 1200, format: "image/jpeg", quality: 0.85 },
  hero: { maxDimension: 1920, format: "image/jpeg", quality: 0.85 },
  logo: { maxDimension: 800, format: "preserve-png", quality: 0.9 },
};

/** Skip recompression if the source PNG is already this small. */
const PNG_PASSTHROUGH_BYTES = 500 * 1024;

/**
 * Compress an image File using the configured preset.
 *
 * Returns a new Blob with the compressed bytes plus the resolved content
 * type. The original File is not mutated.
 */
export async function compressImage(
  file: File,
  preset: CompressionPreset,
): Promise<{ blob: Blob; contentType: string }> {
  const config = PRESETS[preset];

  // Logo passthrough: small PNGs (typical sponsor logos) are kept as-is to
  // preserve transparency without re-encoding artifacts.
  if (
    config.format === "preserve-png" &&
    file.type === "image/png" &&
    file.size <= PNG_PASSTHROUGH_BYTES
  ) {
    return { blob: file, contentType: "image/png" };
  }

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImage(dataUrl);

  const { width, height } = scaleToMaxDimension(
    img.naturalWidth,
    img.naturalHeight,
    config.maxDimension,
  );

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D context unavailable in this browser");
  }
  ctx.drawImage(img, 0, 0, width, height);

  const outputType =
    config.format === "preserve-png" ? "image/jpeg" : config.format;
  const blob = await canvasToBlob(canvas, outputType, config.quality);
  return { blob, contentType: outputType };
}

/**
 * Convert a Blob to a base64 string (no data URI prefix). Used by the
 * setUserPhotoOnBehalf callable, which accepts the compressed image as base64.
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  // Avoid String.fromCharCode.apply on large buffers (call stack risk).
  let binary = "";
  const bytes = new Uint8Array(buf);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binary);
}

// ── internals ──

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image decode failed"));
    img.src = src;
  });
}

function scaleToMaxDimension(
  width: number,
  height: number,
  max: number,
): { width: number; height: number } {
  if (width <= max && height <= max) {
    return { width, height };
  }
  const ratio = width >= height ? max / width : max / height;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Canvas toBlob returned null"));
        }
      },
      type,
      quality,
    );
  });
}
